import { Trash2Icon } from "lucide-react";
import { isEqual } from "es-toolkit/predicate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ProfileAvatar } from "@/features/avatar";
import { getInitials } from "@/features/avatar/avatar.utils";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { Field, FieldLabel } from "@/components/ui/field";
import { ConfirmAlertDialog } from "@/components/ui/confirm-alert-dialog";

export type ContainerSetupValues = {
  name: string;
  slug: string;
  logo_url?: string | null;
  dataEu?: boolean;
};

type Props = {
  containerType: "Workspace" | "Project";
  values: ContainerSetupValues;
  initialValues?: ContainerSetupValues;
  canDelete: boolean;
  canEdit: boolean;
  onChange: (next: ContainerSetupValues) => void;
  onSubmit: () => void;
  onLogoUpload: (url: string) => void | Promise<void>;
  onLogoDelete: () => void | Promise<void>;
  isSubmitting: boolean;
  canSubmit: boolean;
  workspaceSlug?: string;
  settingsForm?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  errorMessage?: string | null;
};

export const LOGO_STORAGE_BUCKET = "workspace-logos";

export function ContainerSetupForm({
  containerType,
  values,
  initialValues,
  canDelete,
  canEdit,
  onChange,
  onSubmit,
  onLogoUpload,
  onLogoDelete,
  isSubmitting,
  canSubmit,
  workspaceSlug,
  settingsForm = false,
  onDelete,
  isDeleting,
  errorMessage,
}: Props) {
  const hasUnsavedChanges = !isEqual(values, initialValues);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-white p-6">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor={containerType == "Workspace" ? "workspace-name" : "project-name"}>
              {containerType} name *
            </Label>
            <Input
              id={containerType == "Workspace" ? "workspace-name" : "project-name"}
              value={values.name}
              onChange={(e) => onChange({ ...values, name: e.target.value })}
              placeholder={
                containerType == "Workspace"
                  ? "Enter the name of your company or team"
                  : "Enter the name of your project"
              }
              autoComplete="organization"
              disabled={!!isSubmitting || !canEdit}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={containerType == "Workspace" ? "workspace-slug" : "project-slug"}>
              {containerType} slug *
            </Label>

            <InputGroup>
              <InputGroupAddon>
                <InputGroupText>
                  {workspaceSlug ? `${workspaceSlug}/projects/` : "app.kairo.pm/workspaces/"}
                </InputGroupText>
              </InputGroupAddon>

              <InputGroupInput
                id={containerType == "Workspace" ? "workspace-slug" : "project-slug"}
                value={values.slug}
                onChange={(e) => onChange({ ...values, slug: e.target.value })}
                placeholder={containerType == "Workspace" ? "my-workspace" : "my-project"}
                disabled={!!isSubmitting || !canEdit}
                className="pl-0.5!"
              />
            </InputGroup>
          </div>
          <Field>
            <div className="mt-3 flex items-center gap-8">
              <ProfileAvatar
                avatarPath={values.logo_url || null}
                uploadLabel={
                  containerType == "Workspace"
                    ? "Select new workspace logo"
                    : "Select new project logo"
                }
                deleteText="Delete logo"
                size={100}
                storageName={LOGO_STORAGE_BUCKET}
                fallbackInitials={getInitials({
                  workspaceName: values.name,
                  avatarFallbackName: containerType == "Workspace" ? "W" : "P",
                })}
                editable={!isSubmitting && !!canEdit}
                onUpload={onLogoUpload}
                onDelete={onLogoDelete}
                className="rounded-lg"
              />
              <div className="flex h-full flex-col justify-between">
                <FieldLabel>{containerType} logo</FieldLabel>
                <span className="text-muted-foreground text-xs">
                  We support PNGs and JPEGs under 5MB.
                </span>
                <span className="text-muted-foreground text-xs">
                  Recommended size is 400x400px.
                </span>
              </div>
            </div>
          </Field>
          {containerType == "Workspace" && values.dataEu !== undefined && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={values.dataEu}
                onChange={(e) => onChange({ ...values, dataEu: e.target.checked })}
                disabled={!!isSubmitting && !canEdit}
              />
              <span className="text-sm">Data hosting in the EU</span>
            </label>
          )}
          {!!errorMessage && (
            <Alert variant="destructive">
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {!!settingsForm && !!canEdit && (
            <div className="mt-10 flex items-center justify-between">
              {!!onDelete && !!canDelete && (
                <ConfirmAlertDialog
                  trigger={
                    <Button
                      className="w-fit"
                      variant="outline-destructive"
                      disabled={!!isSubmitting}
                    >
                      Delete {containerType.toLowerCase()}
                    </Button>
                  }
                  title={`Delete ${containerType.toLowerCase()}?`}
                  description={`This will permanently delete this ${containerType.toLowerCase()} and all of its data.`}
                  onConfirm={onDelete}
                  isPending={isDeleting}
                  icon={<Trash2Icon />}
                />
              )}

              <Button
                className="ml-auto w-1/4"
                onClick={onSubmit}
                disabled={isSubmitting || !canSubmit || !hasUnsavedChanges}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {!settingsForm && (
        <div className="flex items-center justify-end">
          <Button className="w-full" onClick={onSubmit} disabled={isSubmitting || !canSubmit}>
            {isSubmitting ? "Creating…" : "Continue"}
          </Button>
        </div>
      )}
    </div>
  );
}
